import { Box, Menu, Tooltip, MenuItem, IconButton } from '@mui/material'
import { MutableRefObject, useState, useRef, ReactElement } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { setLayout } from '../../redux/actions'
import {
  routes,
  layout,
  constants,
  sidebarTexts,
  sidebarPatientLayout,
} from '../../utils/constants'
import LogoutDialog from './LogoutDialog'
import VideocamIcon from '@mui/icons-material/Videocam'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import DashboardIcon from '@mui/icons-material/Dashboard'
import VitalAlerts from '../Alerts/VitalAlerts/VitalAlerts'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import ThopNotifications from '../Alerts/ThopNotifications/ThopNotifications'
import ProrithmNotifications from '../Prorithm/ProrithmNotifications/ProrithmNotifications'
import prorithm from '../../assets/prorithm.png'
import './Sidebar.css'

const Sidebar = (): ReactElement => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const sidebarButtonRef = useRef() as MutableRefObject<HTMLDivElement>
  const [activeButtonIndex, setActiveButtonIndex] = useState<number>(0)
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [prorithmAnchorEl, setProrithmAnchorEl] = useState<null | HTMLElement>(
    null
  )
  const [openDialog, setOpenDialog] = useState<boolean>(false)

  const openLayout = Boolean(anchorEl)
  const openProrithmMenu = Boolean(prorithmAnchorEl)
  const patientDetails = useSelector((state: any) => ({
    layout: state.layout,
  }))

  const isHomeRoute = location.pathname === routes?.homeRoute

  const shouldRenderVitalAlerts = () => {
    if (isHomeRoute) {
      const layoutCheck =
        patientDetails?.layout === sidebarPatientLayout?.vitals ||
        patientDetails?.layout === sidebarPatientLayout?.default
      return layoutCheck
    }
    return false
  }

  const shouldRenderThopNotification = () => {
    if (isHomeRoute) {
      const layoutCheck =
        patientDetails?.layout === sidebarPatientLayout?.intensivists ||
        patientDetails?.layout === sidebarPatientLayout?.default
      return layoutCheck
    }
    return false
  }

  const shouldRenderProrithmNotifications = () => {
    return location.pathname === routes?.prorithmVitals
  }

  const handleLayoutButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget)
  }

  const handleLayoutClose = () => {
    setAnchorEl(null)
  }

  const handleProrithmButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setProrithmAnchorEl(event.currentTarget)
  }

  const handleProrithmClose = () => {
    setProrithmAnchorEl(null)
  }

  const setPatientDashboardLayout = () => {
    dispatch(setLayout('patientDashboard'))
    setAnchorEl(null)
  }

  const setVitalsLayout = () => {
    dispatch(setLayout('vitals'))
    setAnchorEl(null)
  }

  const setPatientDataLayout = () => {
    dispatch(setLayout('patientData'))
    setAnchorEl(null)
  }

  const setIntensivistsLayout = () => {
    dispatch(setLayout('intensivists'))
    setAnchorEl(null)
  }

  const setProrithmDashboardLayout = (index: number) => {
    setProrithmAnchorEl(null)
    handleSelectedModule(index)
    setActiveButtonIndex(index)
    navigate(routes?.prorithmDashboard)
  }

  const setProrithmVitalsLayout = (index: number) => {
    setProrithmAnchorEl(null)
    handleSelectedModule(index)
    setActiveButtonIndex(index)
    navigate(routes?.prorithmVitals)
  }

  const handleSelectedModule = (index: number) => {
    if (activeButtonIndex !== null) {
      sidebarButtonRef?.current?.children?.[
        activeButtonIndex
      ]?.classList?.remove('sidebar-button-clicked')
    }
    sidebarButtonRef?.current?.children?.[index]?.classList?.add(
      'sidebar-button-clicked'
    )
  }

  const handleHospitalsRoute = (index: number) => {
    handleSelectedModule(index)
    setActiveButtonIndex(index)
    navigate(routes?.homeRoute)
  }

  const handleFullScreen = () => {
    if (isFullScreen) {
      document.exitFullscreen()
    } else {
      document.body.requestFullscreen()
    }
    setIsFullScreen(!isFullScreen)
  }

  const menuOptions = [
    {
      id: 'patient-dashboard-layout',
      onClick: setPatientDashboardLayout,
      label: layout?.patientDashboard,
    },
    {
      id: 'patient-vitals-layout',
      onClick: setVitalsLayout,
      label: layout?.patientVitals,
    },
    {
      id: 'patient-data-layout',
      onClick: setPatientDataLayout,
      label: layout?.patientData,
    },
    {
      id: 'intensivists-layout',
      onClick: setIntensivistsLayout,
      label: layout?.intensivists,
    },
  ]

  const prorithmMenuItems = [
    {
      id: 'prorithm-dashboard',
      onClick: () => setProrithmDashboardLayout(1),
      label: sidebarTexts?.prorithmDashboard,
    },
    {
      id: 'prorithm-vitals',
      onClick: () => setProrithmVitalsLayout(1),
      label: sidebarTexts?.prorithmVitals,
    },
  ]

  return (
    <div className='sidebar-container' id='sidebar-container'>
      <Box className='logo' id='sidebar-logo' onClick={handleFullScreen}>
        <Tooltip
          arrow
          id='screen-size-tooltip'
          title={
            isFullScreen
              ? constants?.exitFullscreen
              : constants?.enterFullScreen
          }
        >
          {isFullScreen ? (
            <FullscreenExitIcon data-testid='exit-fullscreen-icon' />
          ) : (
            <FullscreenIcon data-testid='fullscreen-icon' />
          )}
        </Tooltip>
      </Box>
      <Box>
        <Box display='flex' flexDirection='column'>
          {isHomeRoute && (
            <>
              <Tooltip id='layout-tooltip' title='Select Layout' arrow>
                <IconButton
                  size='large'
                  id='layout-btn'
                  aria-haspopup='true'
                  aria-label='select-layout'
                  onClick={handleLayoutButtonClick}
                  aria-controls={openLayout ? 'basic-menu' : undefined}
                  aria-expanded={openLayout ? 'true' : undefined}
                >
                  <DashboardIcon className='white-button' />
                </IconButton>
              </Tooltip>
              <Menu
                id='layout-menu'
                open={openLayout}
                anchorEl={anchorEl}
                onClose={handleLayoutClose}
              >
                {menuOptions?.map(({ id, onClick, label }) => (
                  <MenuItem key={id} id={id} onClick={onClick}>
                    {label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          <Tooltip id='video-tooltip' title='Video Call' arrow>
            <IconButton
              size='large'
              aria-haspopup='true'
              id='video-call-button'
              aria-label='start-video-call'
              onClick={() =>
                open(routes?.videoCall, '_blank', 'rel=noopener noreferrer')
              }
            >
              <VideocamIcon className='white-button' />
            </IconButton>
          </Tooltip>
        </Box>
        <div
          className='sidebar'
          ref={sidebarButtonRef}
          id='sidebar-buttons-group'
        >
          <Box
            id='hospitals-button'
            onClick={() => handleHospitalsRoute(0)}
            className='sidebar-button sidebar-button-clicked'
          >
            <Tooltip id='hospitals-tooltip' title='Hospitals' arrow>
              <LocalHospitalIcon id='hospitals-icon' />
            </Tooltip>
          </Box>
          <Box id='prorithm-button' className='sidebar-button'>
            <Tooltip id='prorithm-tooltip' title='Prorithm' arrow>
              <IconButton
                aria-haspopup='true'
                id='prorithm-menu-button'
                aria-label='prorithm-menu'
                onClick={handleProrithmButtonClick}
                aria-expanded={openProrithmMenu ? 'true' : undefined}
                aria-controls={openProrithmMenu ? 'basic-menu' : undefined}
              >
                <img src={prorithm} alt='prorithm' height={35} width={35} />
              </IconButton>
            </Tooltip>
            <Menu
              id='prorithm-menu'
              open={openProrithmMenu}
              anchorEl={prorithmAnchorEl}
              onClose={handleProrithmClose}
            >
              {prorithmMenuItems?.map(({ id, onClick, label }) => (
                <MenuItem key={id} id={id} onClick={onClick}>
                  {label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </div>
        {shouldRenderVitalAlerts() && <VitalAlerts />}
        {shouldRenderThopNotification() && <ThopNotifications />}
        {shouldRenderProrithmNotifications() && <ProrithmNotifications />}
      </Box>
      <div className='sidebar-bottom-button-set'>
        <Box
          id='logout-dialog'
          data-testid='logout-icon'
          className='sidebar-button'
          onClick={() => setOpenDialog(true)}
        >
          <Tooltip id='logout-tooltip' title='Logout' arrow>
            <ExitToAppIcon id='logout-icon' />
          </Tooltip>
        </Box>
        <LogoutDialog open={openDialog} setOpen={setOpenDialog} />
      </div>
    </div>
  )
}

export default Sidebar
